// TEMPORARY MOCK ADAPTER for Patient Inquiry Management (PRD 12).
// In-memory only — state lives in this module's `inquiries` array and resets on
// hard reload. Mutations resolve with ~300 ms simulated latency. Errors mirror
// the BE envelope in docs/api-contracts/patient-inquiry-management.md (canonical
// codes + top-level message for toasts).
// Backend switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter.

import {
  PatientInquiryApiError,
  type CreatePatientInquiryReplyInput,
  type ListPatientInquiriesParams,
  type ListPatientInquiriesResult,
  type PatientInquiryApi,
  type PatientInquiryDetail,
  type PatientInquiryGender,
  type PatientInquiryReply,
  type PatientInquirySortKey,
  type PatientInquiryStatus,
  type PatientInquiryType,
  type RevealInquirerPhoneResult,
} from '../types/patient-inquiry.types';

const LATENCY_MS = 300;
const REPLY_MAX = 2000;

// Mask a phone for list/detail display (mirrors the BE: prefix + real last 4, middle masked → 010-****-5678).
// The internal `inquiries` array keeps the FULL number; only the projection masks it. getInquirerPhone returns full.
function maskPhone(phone: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '****';
  const last4 = digits.slice(-4);
  const prefix = digits.length >= 7 ? digits.slice(0, 3) : '';
  return prefix ? `${prefix}-****-${last4}` : `****-${last4}`;
}

function maskRow<T extends { inquirerPhone: string | null }>(row: T): T {
  row.inquirerPhone = maskPhone(row.inquirerPhone);
  return row;
}
// PRD §3.3 — mock "current admin" stamped as the reply author. The real BE
// derives this from the authenticated session.
const MOCK_AUTHOR = '강현우';

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ── Seed ──────────────────────────────────────────────────────────────
// Ported from prototype/js/mock-data.js MockData.patientInquiries. Wire
// normalization: gender already 'M'/'F'; birthDate YYYY.MM.DD → YYYY-MM-DD;
// inquiredAt/replies → ISO datetime; uid kept as "U-{id}".

interface SeedReply {
  author: string;
  createdAt: string; // ISO datetime
  content: string;
}

interface SeedRow {
  id: number;
  uid: string;
  inquirerName: string;
  gender: PatientInquiryGender | null;
  birthDate: string | null; // YYYY-MM-DD
  inquirerPhone: string | null;
  title: string;
  type: PatientInquiryType;
  inquiredAt: string; // ISO datetime
  status: PatientInquiryStatus;
  isWithdrawn?: boolean;
  content: string;
  replies?: SeedReply[];
}

const SEED_ROWS: readonly SeedRow[] = [
  {
    id: 60, uid: 'U-102', inquirerName: '김철수', gender: 'M', birthDate: '1969-08-02',
    inquirerPhone: '010-2244-8190', title: '앱 로그인이 안 됩니다', type: 'ERROR_REPORT',
    inquiredAt: '2026-03-19T11:20:00.000Z', status: 'PENDING',
    content: '어제부터 앱에 로그인이 되지 않습니다. 비밀번호를 재설정해도 동일한 오류가 발생합니다.',
  },
  {
    id: 59, uid: 'U-88', inquirerName: '이영희', gender: 'F', birthDate: '1986-10-05',
    inquirerPhone: '010-3391-7745', title: 'PRO 설문 알림이 오지 않아요', type: 'SERVICE_INQUIRY',
    inquiredAt: '2026-03-18T15:30:00.000Z', status: 'ANSWERED',
    content: 'PRO 설문 알림이 설정되어 있는데 알림이 오지 않습니다.',
    replies: [
      { author: '강현우', createdAt: '2026-03-19T09:00:00.000Z', content: '안녕하세요. 앱 알림 설정을 확인해 주세요. 휴대폰 설정 > 알림에서 스마트홈케어 앱 알림이 허용되어 있는지 확인 부탁드립니다.' },
    ],
  },
  {
    id: 58, uid: 'U-75', inquirerName: '박민준', gender: 'M', birthDate: '1978-03-15',
    inquirerPhone: '010-7788-2231', title: '건강백과 콘텐츠 오류 신고', type: 'ERROR_REPORT',
    inquiredAt: '2026-03-17T14:00:00.000Z', status: 'PENDING',
    content: "건강백과 '규칙적인 수면 습관 만들기' 콘텐츠에서 이미지가 표시되지 않습니다.",
  },
  {
    id: 57, uid: 'U-61', inquirerName: '최수연', gender: 'F', birthDate: '1992-07-22',
    inquirerPhone: '010-5567-1102', title: '담당 의료진 변경 요청', type: 'SERVICE_INQUIRY',
    inquiredAt: '2026-03-16T10:45:00.000Z', status: 'ANSWERED',
    content: '담당 의료진이 변경되었는데 앱에서 업데이트가 되지 않습니다.',
    replies: [
      { author: '강현우', createdAt: '2026-03-16T16:00:00.000Z', content: '안녕하세요. 담당 의료진 변경은 소속 기관에 문의해 주세요. 기관에서 처리 후 앱에 반영됩니다.' },
    ],
  },
  {
    id: 56, uid: 'U-54', inquirerName: '정우성', gender: 'M', birthDate: '1981-12-11',
    inquirerPhone: '010-9923-4417', title: '복약 알림 시간을 바꾸고 싶어요', type: 'SERVICE_INQUIRY',
    inquiredAt: '2026-03-15T09:05:00.000Z', status: 'PENDING',
    content: '복약 알림이 아침 8시로 되어 있는데 저녁 9시로 변경하고 싶습니다. 방법을 알려주세요.',
  },
  {
    id: 55, uid: 'U-50', inquirerName: '한지민', gender: 'F', birthDate: '1990-02-28',
    inquirerPhone: '010-1144-6620', title: '생체데이터 동기화 오류', type: 'ERROR_REPORT',
    inquiredAt: '2026-03-14T18:42:00.000Z', status: 'ANSWERED',
    content: '웨어러블 기기와 앱이 동기화되지 않습니다. 걸음 수가 0으로 표시됩니다.',
    replies: [
      { author: '강현우', createdAt: '2026-03-15T10:00:00.000Z', content: '안녕하세요. 기기 블루투스 연결을 해제 후 재연결해 주세요. 그래도 동기화되지 않으면 앱을 재설치 부탁드립니다.' },
      { author: '강현우', createdAt: '2026-03-15T14:20:00.000Z', content: '추가로 안내드립니다. 기기 펌웨어가 최신인지도 확인 부탁드립니다.' },
    ],
  },
  {
    id: 54, uid: 'U-47', inquirerName: '오세훈', gender: 'M', birthDate: '1975-06-30',
    inquirerPhone: '010-3312-9087', title: '회원 탈퇴는 어떻게 하나요', type: 'SERVICE_INQUIRY',
    inquiredAt: '2026-03-13T11:15:00.000Z', status: 'PENDING',
    content: '서비스를 더 이상 이용하지 않으려고 합니다. 회원 탈퇴 절차를 알려주세요.',
  },
  {
    id: 53, uid: 'U-39', inquirerName: '강민경', gender: 'F', birthDate: '1995-09-17',
    inquirerPhone: '010-6678-3344', title: '설문 응답 후 화면이 멈춰요', type: 'ERROR_REPORT',
    inquiredAt: '2026-03-12T16:50:00.000Z', status: 'PENDING',
    content: 'PRO 설문을 끝까지 응답하고 제출 버튼을 누르면 화면이 멈춥니다.',
  },
  {
    id: 52, uid: 'U-33', inquirerName: '윤재호', gender: 'M', birthDate: '1968-04-09',
    inquirerPhone: '010-2256-7711', title: '글씨 크기를 키울 수 있나요', type: 'SERVICE_INQUIRY',
    inquiredAt: '2026-03-11T08:30:00.000Z', status: 'ANSWERED',
    content: '앱 글씨가 작아서 보기 어렵습니다. 글씨 크기 조절이 가능한가요?',
    replies: [
      { author: '강현우', createdAt: '2026-03-11T13:40:00.000Z', content: '안녕하세요. 휴대폰 자체 글꼴 크기 설정을 따르도록 되어 있습니다. 설정 > 디스플레이 > 글자 크기에서 조절해 주세요.' },
    ],
  },
  {
    id: 51, uid: 'U-28', inquirerName: '서지우', gender: 'F', birthDate: '1988-11-23',
    inquirerPhone: '010-8890-1267', title: '알림이 너무 자주 옵니다', type: 'SERVICE_INQUIRY',
    inquiredAt: '2026-03-10T20:10:00.000Z', status: 'PENDING',
    content: '하루에 알림이 여러 번 와서 부담스럽습니다. 알림 빈도를 줄일 수 있나요?',
  },
  {
    id: 50, uid: 'U-21', inquirerName: '(탈퇴한 사용자)', gender: null, birthDate: null,
    inquirerPhone: null, title: '결제 내역 문의', type: 'SERVICE_INQUIRY',
    inquiredAt: '2026-03-09T13:25:00.000Z', status: 'PENDING', isWithdrawn: true,
    content: '지난달 결제 내역이 앱에서 보이지 않습니다. 확인 부탁드립니다.',
  },
  {
    id: 49, uid: 'U-14', inquirerName: '임도현', gender: 'M', birthDate: '1972-01-19',
    inquirerPhone: '010-4423-8855', title: '앱이 자꾸 종료됩니다', type: 'ERROR_REPORT',
    inquiredAt: '2026-03-08T10:00:00.000Z', status: 'ANSWERED',
    content: '앱을 실행하면 몇 초 후 자동으로 종료됩니다. 기종은 갤럭시 S21입니다.',
    replies: [
      { author: '강현우', createdAt: '2026-03-08T15:30:00.000Z', content: '안녕하세요. 불편을 드려 죄송합니다. 앱 최신 버전(v2.1) 업데이트 후에도 동일한지 확인 부탁드립니다.' },
    ],
  },
];

// Module-level mutable state. `replies` get sequential ids per inquiry.
let replyIdSeq = 1000;
const inquiries: PatientInquiryDetail[] = SEED_ROWS.map((row) => ({
  id: row.id,
  uid: row.uid,
  inquirerName: row.inquirerName,
  gender: row.gender,
  birthDate: row.birthDate,
  inquirerPhone: row.inquirerPhone,
  title: row.title,
  type: row.type,
  inquiredAt: row.inquiredAt,
  status: row.status,
  isWithdrawn: row.isWithdrawn ?? false,
  content: row.content,
  replies: (row.replies ?? []).map<PatientInquiryReply>((r) => ({
    id: (replyIdSeq += 1),
    author: r.author,
    createdAt: r.createdAt,
    content: r.content,
  })),
}));

// ── Helpers ─────────────────────────────────────────────────────────

// PRD §3.2 keyword search — mirrors what the real backend can actually match, so dev-mode behaves like
// production. Name and title match on any fragment; PHONE matches only the WHOLE number or its LAST 4 DIGITS.
// The phone is stored encrypted server-side, so it is searchable only through the two fingerprints that exist
// (full number, last 4). A middle slice such as "1111" of 010-1111-2222 therefore matches nothing — a mock that
// used includes() on the phone would show dev results production could never return.
function matchesKeyword(item: PatientInquiryDetail, keyword: string): boolean {
  const q = keyword.trim().toLowerCase();
  if (!q) return true;
  return (
    (item.inquirerName ?? '').toLowerCase().includes(q) ||
    matchesPhone(item.inquirerPhone, q) ||
    item.title.toLowerCase().includes(q)
  );
}

/** Phone match: whole number (ignoring formatting) or exactly its last 4 digits. Nothing in between. */
function matchesPhone(phone: string | null, query: string): boolean {
  const phoneDigits = digitsOf(phone ?? '');
  const queryDigits = digitsOf(query);
  if (!phoneDigits || !queryDigits) return false;
  if (queryDigits === phoneDigits) return true;
  return queryDigits.length === 4 && phoneDigits.endsWith(queryDigits);
}

function digitsOf(value: string): string {
  return value.replace(/\D/g, '');
}

function sortValue(item: PatientInquiryDetail, key: PatientInquirySortKey): string | number {
  switch (key) {
    case 'id':
      return item.id;
    case 'uid':
      return item.uid;
    case 'inquirerName':
      return item.inquirerName ?? '';
    case 'title':
      return item.title;
    case 'type':
      return item.type;
    case 'inquiredAt':
      return item.inquiredAt;
    case 'status':
      return item.status;
    default:
      return item.inquiredAt;
  }
}

// ── API ─────────────────────────────────────────────────────────────

export const patientInquiryMockApi: PatientInquiryApi = {
  async listPatientInquiries(
    params: ListPatientInquiriesParams,
  ): Promise<ListPatientInquiriesResult> {
    await delay();
    const { keyword = '', page, perPage, sortBy, sortDir } = params;

    const filtered = inquiries.filter((item) => matchesKeyword(item, keyword));

    filtered.sort((a, b) => {
      const va = sortValue(a, sortBy);
      const vb = sortValue(b, sortBy);
      let cmp: number;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    const total = filtered.length;
    const start = (page - 1) * perPage;
    const items = filtered
      .slice(start, start + perPage)
      // Strip the detail-only fields from the list projection; mask the phone (full only via getInquirerPhone).
      .map(({ content: _content, replies: _replies, ...row }) => maskRow(clone(row)));

    return { items, total, page, perPage };
  },

  async getPatientInquiry(id: number): Promise<PatientInquiryDetail> {
    await delay();
    const found = inquiries.find((i) => i.id === id);
    if (!found) {
      throw new PatientInquiryApiError('NOT_FOUND', '문의를 찾을 수 없습니다.');
    }
    return maskRow(clone(found));
  },

  async getInquirerPhone(id: number): Promise<RevealInquirerPhoneResult> {
    await delay();
    const found = inquiries.find((i) => i.id === id);
    if (!found) {
      throw new PatientInquiryApiError('NOT_FOUND', '문의를 찾을 수 없습니다.');
    }
    // The on-demand reveal returns the FULL number (the internal seed keeps it unmasked).
    return { phone: found.inquirerPhone };
  },

  async replyPatientInquiry(
    id: number,
    input: CreatePatientInquiryReplyInput,
  ): Promise<PatientInquiryDetail> {
    await delay();
    const content = input.replyContent?.trim() ?? '';
    if (!content) {
      throw new PatientInquiryApiError(
        'REPLY_REQUIRED',
        '답변 내용을 입력해주세요.',
        { fields: { replyContent: '답변 내용을 입력해주세요.' } },
      );
    }
    if (content.length > REPLY_MAX) {
      throw new PatientInquiryApiError(
        'REPLY_TOO_LONG',
        '답변은 2,000자 이내로 입력해주세요.',
        { fields: { replyContent: '답변은 2,000자 이내로 입력해주세요.' } },
      );
    }

    const target = inquiries.find((i) => i.id === id);
    if (!target) {
      throw new PatientInquiryApiError('NOT_FOUND', '문의를 찾을 수 없습니다.');
    }

    target.replies.push({
      id: (replyIdSeq += 1),
      author: MOCK_AUTHOR,
      // ISO instant; the FE formats to YYYY.MM.DD HH:MM in local time.
      createdAt: new Date().toISOString(),
      content,
    });
    // PRD §3.3 step 10: first reply on a PENDING inquiry flips it to ANSWERED.
    target.status = 'ANSWERED';

    return maskRow(clone(target));
  },
};
